// src/pages/Accounting/Receipts/components/ViewDrawer/ReceiptImage.tsx

import type React from "react";
import type { Receipt } from "../../types";
import { PALETTE } from "../../constants";
import { formatDateLong, totalTaxForReceipt } from "../../utils";

interface ReceiptImageProps {
  receipt: Receipt;
}

export function ReceiptImage({ receipt }: ReceiptImageProps) {
  if (receipt.imageUrl) {
    return (
      <div style={{ marginBottom: 6 }}>
        <img
          src={receipt.imageUrl}
          alt={`Receipt from ${receipt.vendorName}`}
          style={{
            width: "100%",
            borderRadius: 2,
            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          }}
        />
        <OpenFullSizeLink url={receipt.imageUrl} />
      </div>
    );
  }

  if (receipt.pdfUrl) {
    return (
      <div style={{ marginBottom: 6 }}>
        <div
          style={{
            background: "#FEFCF7",
            borderRadius: 2,
            padding: "28px 20px",
            fontFamily: "'Courier New', Menlo, monospace",
            fontSize: 12,
            color: PALETTE.ink2,
            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            textAlign: "center",
          }}
        >
          PDF receipt
        </div>
        <OpenFullSizeLink url={receipt.pdfUrl} />
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <PaperPreview receipt={receipt} />
    </div>
  );
}

function PaperPreview({ receipt }: { receipt: Receipt }) {
  const subtotal = receipt.total - totalTaxForReceipt(receipt.tax);
  const taxEntries: Array<[string, number]> = [];
  if (receipt.tax.gst) taxEntries.push(["GST", receipt.tax.gst]);
  if (receipt.tax.hst) taxEntries.push(["HST", receipt.tax.hst]);
  if (receipt.tax.pst) taxEntries.push(["PST", receipt.tax.pst]);
  if (receipt.tax.other) taxEntries.push(["Other tax", receipt.tax.other]);

  return (
    <div
      style={{
        background: "#FEFCF7",
        borderRadius: 2,
        padding: "22px 20px",
        fontFamily: "'Courier New', Menlo, monospace",
        fontSize: 11,
        color: PALETTE.ink2,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        lineHeight: 1.55,
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: PALETTE.ink,
          marginBottom: 10,
        }}
      >
        {receipt.vendorName || "Unknown vendor"}
      </div>
      <div style={rowStyle}>
        <span>{formatDateLong(receipt.date)}</span>
      </div>
      <div style={sepStyle} />
      {receipt.lineItems && receipt.lineItems.length > 0 ? (
        <>
          {receipt.lineItems.map((item, i) => (
            <div key={i} style={rowStyle}>
              <span>{item.name}</span>
              <span>{item.amount.toFixed(2)}</span>
            </div>
          ))}
          <div style={sepStyle} />
        </>
      ) : null}
      <div style={rowStyle}>
        <span>Subtotal</span>
        <span>{subtotal.toFixed(2)}</span>
      </div>
      {taxEntries.map(([label, amount]) => (
        <div key={label} style={rowStyle}>
          <span>{label}</span>
          <span>{amount.toFixed(2)}</span>
        </div>
      ))}
      <div
        style={{
          ...rowStyle,
          fontWeight: 700,
          fontSize: 11.5,
          color: PALETTE.ink,
          marginTop: 4,
        }}
      >
        <span>TOTAL</span>
        <span>{receipt.total.toFixed(2)}</span>
      </div>
    </div>
  );
}

function OpenFullSizeLink({ url }: { url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: "block",
        margin: "4px 0 20px",
        fontSize: 12,
        color: PALETTE.velaDeep,
        fontWeight: 600,
        textDecoration: "none",
        textAlign: "right",
      }}
    >
      Open full size &rarr;
    </a>
  );
}

const rowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  padding: "2px 0",
};

const sepStyle: React.CSSProperties = {
  borderTop: `1px dashed ${PALETTE.ink4}`,
  margin: "8px 0",
};